import { useEffect, useRef, useState } from 'react'
import { FetchPolicy } from '@apollo/client'
import { ApolloQueryResult } from '@apollo/client'
import gql from 'graphql-tag'
import { apolloClient } from '@/state/apollo-client'
import { useFormContext } from '@/state/main-form/reducer'
import {
	LlmResponseHtmlPollQuery,
	LlmResponseHtmlPollQueryVariables,
	LlmResponseHtmlPubPollQuery,
	LlmResponseHtmlPubPollQueryVariables,
} from './__gen/useLlmResponseHtmlPoll'

const POLL_INTERVAL_ACTIVE = 4_000
const POLL_INTERVAL_IDLE = 60_000
const IDLE_THRESHOLD = 15 * 60 * 1_000

const LLM_RESPONSE_HTML_POLL_QUERY = gql`
	query llmResponseHtmlPoll($formId: ID!, $recordId: ID!) {
		llmResponseHtml_List(form: $formId, record: $recordId) {
			objects {
				id
				html
			}
		}
	}
`

const LLM_RESPONSE_HTML_PUB_POLL_QUERY = gql`
	query llmResponseHtmlPubPoll($formId: ID!, $recordId: ID!) {
		llmResponseHtmlPub_List(form: $formId, record: $recordId) {
			objects {
				id
				html
			}
		}
	}
`

export const useLlmResponseHtmlPoll = (formId: string, recordId: string) => {
	const lastActivityRef = useRef(Date.now())
	const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)
	const formContext = useFormContext()
	const [html, setHtml] = useState<string | null>(null)

	useEffect(() => {
		const updateActivity = () => {
			lastActivityRef.current = Date.now()
		}
		window.addEventListener('mousemove', updateActivity)
		window.addEventListener('keydown', updateActivity)
		window.addEventListener('click', updateActivity)
		return () => {
			window.removeEventListener('mousemove', updateActivity)
			window.removeEventListener('keydown', updateActivity)
			window.removeEventListener('click', updateActivity)
		}
	}, [])

	useEffect(() => {
		const poll = async () => {
			try {
				let result: ApolloQueryResult<LlmResponseHtmlPubPollQuery | LlmResponseHtmlPollQuery>
				const common: { variables: { formId: string; recordId: string }; fetchPolicy: FetchPolicy } = {
					variables: { formId, recordId },
					fetchPolicy: 'network-only',
				}

				if (formContext.publicForm) {
					result = await apolloClient.query<
						LlmResponseHtmlPubPollQuery,
						LlmResponseHtmlPubPollQueryVariables
					>({
						...common,
						query: LLM_RESPONSE_HTML_PUB_POLL_QUERY,
						context: {
							headers: formContext.publicForm ? { 'X-Public-Form-Code': formContext.publicForm.code } : {},
						},
					})
				} else {
					result = await apolloClient.query<LlmResponseHtmlPollQuery, LlmResponseHtmlPollQueryVariables>({
						...common,
						query: LLM_RESPONSE_HTML_POLL_QUERY,
					})
				}

				if (!result.data) {
					console.error('no data')
					return
				}

				const html =
					'llmResponseHtml_List' in result.data ?
						result.data.llmResponseHtml_List?.objects?.[0]?.html
					:	result.data.llmResponseHtmlPub_List?.objects?.[0]?.html
				setHtml(html || null)
			} catch {
				// silently ignore transient polling errors
			}

			const isIdle = Date.now() - lastActivityRef.current > IDLE_THRESHOLD
			timeoutRef.current = setTimeout(poll, isIdle ? POLL_INTERVAL_IDLE : POLL_INTERVAL_ACTIVE)
		}
		poll()

		return () => {
			if (timeoutRef.current !== null) {
				clearTimeout(timeoutRef.current)
			}
		}
	}, [formId, recordId])

	return html
}
